import React, { useState, useEffect } from 'react';
import { FileText, Clock, User, ArrowRight, Activity, Calendar, TrendingUp, AlertCircle, CheckCircle2, XCircle, BarChart2, Layers } from 'lucide-react';
import { useParams, useLocation } from 'react-router-dom';
import { leadsAPI, manageDealsAPI, managerAPI } from '../../../api/apiHelper';

export default function DealOverview() {
    const { id } = useParams();
    const location = useLocation();
    const [overviewData, setOverviewData] = useState(null);
    const [activeTab, setActiveTab] = useState('executive');
    const [activities, setActivities] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(false);

    // Activity History (deals by customer)
    const [dealHistory, setDealHistory] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        const fetchOverview = async () => {
            if (!id || id === 'view') return;
            try {
                const response = await leadsAPI.getDealOverview(id);
                console.log("Deal Overview Response:", response);
                if (response && response.data) {
                    setOverviewData(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch deal overview:", error);
            }
        };
        fetchOverview();
    }, [id]);

    useEffect(() => {
        if (activeTab === 'activity' && id && id !== 'view') {
            fetchActivities();
        }
        if (activeTab === 'history' && overviewData) {
            fetchDealHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, id, overviewData]);

    const fetchActivities = async () => {
        setLoadingActivities(true);
        try {
            const response = await manageDealsAPI.getDealActivities(id);
            if (response.success && response.activities) {
                setActivities(response.activities);
            }
        } catch (error) {
            console.error("Failed to fetch activities", error);
        } finally {
            setLoadingActivities(false);
        }
    };

    const fetchDealHistory = async () => {
        // Prefer customerId passed via navigation state (ManageLeads → LeadDetail),
        // then fall back to whatever the overview API returned.
        // Also supports campaignId as fallback.
        const customerId =
            location.state?.customerId ||
            overviewData?.customerId ||
            overviewData?.customer?.customerId ||
            overviewData?.customer?.id;

        // Also check for campaignId as fallback
        const campaignId =
            overviewData?.campaignId ||
            overviewData?.campaign?.id ||
            overviewData?.campaign?.campaignId;

        // Use customerId first, then campaignId
        const queryId = customerId || campaignId;

        if (!queryId) {
            console.warn('Deal History: Neither customerId nor campaignId available yet, skipping fetch.');
            return;
        }

        console.log('Fetching deal history with:', { customerId, campaignId, queryId });

        setLoadingHistory(true);
        try {
            const response = await managerAPI.getDealsByCustomer(queryId);
            // apiRequest returns response.data (the shape is { customerId, customerName, totalDeals, deals, ... })
            const payload = response?.data || response;
            console.log('Deal history response:', payload);
            setDealHistory(payload);
        } catch (error) {
            console.error("Failed to fetch deal history:", error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const getRiskColor = (score) => {
        if (score === null || score === undefined) return '#6b7280';
        const s = Number(score);
        if (isNaN(s)) return '#6b7280';
        if (s < 40) return '#22c55e';
        if (s < 70) return '#eab308';
        return '#ef4444';
    };

    const getRiskLabel = (score) => {
        if (score === null || score === undefined || isNaN(Number(score))) return null;
        const s = Number(score);
        if (s < 40) return 'Low';
        if (s < 70) return 'Medium';
        return 'High';
    };

    const formatCurrency = (value) => {
        if (value === undefined || value === null) return '$0';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No input';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'No input';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatActivityDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatFieldName = (field) => {
        const niceNames = {
            'documentInfo': 'Document Updates',
            'opportunityName': 'Opportunity',
            'riskScore': 'Risk Score',
            'winProbability': 'Win Probability',
            'dealId': 'ID',
            'associatedLead': 'Contact',
            'documentStatus': 'Status',
            'nextAction': 'Next Step',
            'notes': 'Notes',
            'assignedTo': 'Assigned To',
            'assignedToUser': 'Assigned To',
            'stageName': 'Stage',
            'dealStage': 'Stage',
            'dealStatus': 'Deal Status',
            'engagementScore': 'Engagement Score',
            'riskLevel': 'Risk Level',
            'priority': 'Priority',
            'description': 'Description',
            'dealValue': 'Deal Value',
            'expectedCloseDate': 'Expected Close',
            'customerName': 'Customer',
            'companyName': 'Company'
        };
        if (niceNames[field]) return niceNames[field];
        return field
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    const getVal = (val) => {
        if (val === null || val === undefined) return <span style={ { color: '#9ca3af', fontStyle: 'italic' } }>No input</span>;

        const prettify = (str) => {
            if (typeof str !== 'string') return str;
            const s = str.trim();
            if (s === '') return s;
            // Detect and format ISO date strings
            if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
                const d = new Date(s);
                if (!isNaN(d.getTime())) {
                    const hasTime = s.includes('T') || s.includes(':');
                    const datePart = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                    if (hasTime) {
                        const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        return `${datePart} · ${timePart}`;
                    }
                    return datePart;
                }
            }
            // Prettify snake_case identifiers
            if (/^[a-z0-9_]+$/.test(s) && s.includes('_')) {
                return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            }
            return s;
        };

        const beautify = (v) => {
            if (v === null || v === undefined) return null;
            if (typeof v === 'string') {
                const trimmed = v.trim();
                if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                    try { return beautify(JSON.parse(trimmed)); } catch (e) { return prettify(trimmed); }
                }
                return prettify(trimmed);
            }
            if (typeof v === 'object') {
                if (Array.isArray(v)) return v.map(item => beautify(item)).filter(i => i !== null).join(', ');
                if (v.name && typeof v.name !== 'object') return prettify(String(v.name));
                if (v.label && typeof v.label !== 'object') return prettify(String(v.label));
                if (v.displayName && typeof v.displayName !== 'object') return prettify(String(v.displayName));
                if (v.value !== undefined && typeof v.value !== 'object') return prettify(String(v.value));

                const entries = Object.entries(v).filter(([k]) => !['id', '_id', '__v'].includes(k.toLowerCase()));
                if (entries.length === 0) return null;
                const simple = entries.find(([k, val]) => typeof val !== 'object' && val !== null);
                if (simple) return prettify(String(simple[1]));
                return JSON.stringify(v);
            }
            return prettify(String(v));
        };

        const result = beautify(val);
        if (result === null || result === '' || result === undefined) return <span style={ { color: '#9ca3af', fontStyle: 'italic' } }>No input</span>;
        return result;
    };

    const data = overviewData || {};

    // Stage status badge
    const STAGE_STATUS_STYLES = {
        demo_scheduled: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe', label: 'Demo Scheduled' },
        not_interested: { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca', label: 'Not Interested' },
        customer: { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0', label: 'Customer' },
        opportunity: { bg: '#faf5ff', text: '#7c3aed', border: '#e9d5ff', label: 'Opportunity' },
        new_lead: { bg: '#f0f9ff', text: '#0369a1', border: '#bae6fd', label: 'New Lead' },
        vehicle_delivered: { bg: '#f0fdf4', text: '#166534', border: '#86efac', label: 'Vehicle Delivered' },
        final_drop: { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa', label: 'Final Drop' },
    };

    const getStageStyle = (stageStatus) => {
        const key = (stageStatus || '').toLowerCase();
        return STAGE_STATUS_STYLES[key] || { bg: '#f3f4f6', text: '#374151', border: '#d1d5db', label: stageStatus || 'Unknown' };
    };

    // MEDDIC fields config
    const MEDDIC_FIELDS = [
        { key: 'metrics', short: 'M', label: 'Metrics' },
        { key: 'economic_buyer', short: 'E', label: 'Economic Buyer' },
        { key: 'decision_criteria', short: 'D', label: 'Decision Criteria' },
        { key: 'decision_process', short: 'D', label: 'Decision Process' },
        { key: 'identified_pain', short: 'I', label: 'Identify Pain' },
        { key: 'champion', short: 'C', label: 'Champion' },
    ];

    // Tab bar shared style
    const tabStyle = (tab) => ({
        paddingBottom: '8px',
        cursor: 'pointer',
        borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
        color: activeTab === tab ? '#3b82f6' : '#6b7280',
        fontWeight: 600,
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        whiteSpace: 'nowrap',
    });

    return (
        <div style={ { display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '4px' } }>

            {/* Top Metric Cards */ }
            <div style={ { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '8px', flexShrink: 0 } }>
                <div style={ { background: '#3b82f6', borderRadius: '8px', padding: '12px', height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#fff', overflow: 'hidden', minWidth: 0 } }>
                    <div style={ { fontSize: '12px', fontWeight: 500, opacity: 0.9 } }>Deal Value</div>
                    <div style={ { fontSize: '16px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } title={ formatCurrency(data.value) }>{ formatCurrency(data.value) }</div>
                </div>
                <div style={ { background: '#8b5cf6', borderRadius: '8px', padding: '12px', height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#fff', overflow: 'hidden', minWidth: 0 } }>
                    <div style={ { fontSize: '12px', fontWeight: 500, opacity: 0.9 } }>Win Probability</div>
                    <div style={ { fontSize: '20px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }>{ data.winProbability || '0%' }</div>
                </div>
                <div style={ { background: '#f59e0b', borderRadius: '8px', padding: '12px', height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#fff' } }>
                    <div style={ { fontSize: '12px', fontWeight: 500, opacity: 0.9 } }>Sales Stage</div>
                    <div style={ { fontSize: '16px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }>{ data.stageName || 'Unknown' }</div>
                </div>
                <div style={ { background: getRiskColor(data.riskScore), borderRadius: '8px', padding: '12px', height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#fff', overflow: 'hidden', minWidth: 0 } }>
                    <div style={ { fontSize: '12px', fontWeight: 500, opacity: 0.9 } }>Risk Score</div>
                    <div style={ { fontSize: '20px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }>
                        { (data.riskScore !== undefined && data.riskScore !== null) ? data.riskScore : <span style={ { fontSize: '16px', opacity: 0.7, fontStyle: 'italic', fontWeight: 400 } }>No input</span> }
                    </div>
                </div>
            </div>

            {/* Detail Grid & Summary */ }
            <div style={ { background: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '2px' } }>

                {/* Second Row of Boxes */ }
                <div style={ { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', flexShrink: 0, marginBottom: '14px' } }>
                    <div style={ { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' } }>
                        <div style={ { fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' } }>Close Date</div>
                        <div style={ { fontSize: '15px', color: '#111827', fontWeight: 600 } }>{ formatDate(data.closedDate) }</div>
                    </div>
                    <div style={ { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' } }>
                        <div style={ { fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' } }>Next Activity</div>
                        <div style={ { fontSize: '15px', color: '#111827', fontWeight: 600 } }>{ formatDate(data.nextActivity) }</div>
                    </div>
                    <div style={ { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' } }>
                        <div style={ { fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' } }>Number of calls</div>
                        <div style={ { fontSize: '15px', color: '#111827', fontWeight: 600 } }>{ data.numberOfCalls || 0 }</div>
                    </div>
                    <div style={ { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', height: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' } }>
                        <div style={ { fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' } }>Lost Deal Date</div>
                        <div style={ { fontSize: '15px', color: '#111827', fontWeight: 600 } }>{ formatDate(data.lostDate) }</div>
                    </div>
                </div>

                <div style={ { borderTop: '1px solid #f3f4f6', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' } }>

                    {/* Tabs Header */ }
                    <div style={ { display: 'flex', gap: '24px', borderBottom: '1px solid #e5e7eb', marginBottom: '8px', paddingLeft: '4px', overflowX: 'auto' } }>
                        <div onClick={ () => setActiveTab('executive') } style={ tabStyle('executive') }>
                            Executive Summary
                        </div>
                        <div onClick={ () => setActiveTab('activity') } style={ tabStyle('activity') }>
                            Activity History
                        </div>
                        <div onClick={ () => setActiveTab('history') } style={ tabStyle('history') }>
                            <BarChart2 size={ 13 } />
                            Deal Log
                        </div>
                    </div>

                    <div style={ { fontSize: '13px', color: '#374151', lineHeight: '1.6', background: 'transparent', padding: '0px', display: 'flex', flexDirection: 'column' } }>

                        {/* === Executive Summary === */ }
                        { activeTab === 'executive' && (
                            <div style={ { padding: '16px' } }>
                                { (() => {
                                    let finalSummary = "No input";
                                    let finalKeyPoints = [];
                                    try {
                                        let summaryString = data.latestMeetingSummary?.summary;
                                        if (summaryString) {
                                            summaryString = summaryString.replace(/^```json\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim();
                                            summaryString = summaryString.replace(/\/code\//g, '');
                                            let parsedJson = null;
                                            try { parsedJson = JSON.parse(summaryString); } catch (e) { }
                                            if (parsedJson && parsedJson.meeting_summary) {
                                                finalSummary = parsedJson.meeting_summary.summary || "No summary available.";
                                                finalKeyPoints = parsedJson.meeting_summary.key_points || [];
                                            } else if (Array.isArray(data.latestMeetingSummary?.key_points)) {
                                                finalSummary = summaryString;
                                                finalKeyPoints = data.latestMeetingSummary.key_points;
                                            } else {
                                                finalSummary = summaryString;
                                                finalKeyPoints = [];
                                            }
                                        }
                                    } catch (e) { console.error('Failed to parse meeting summary:', e); }
                                    return (
                                        <>
                                            <div style={ { marginBottom: '12px' } }>{ finalSummary }</div>
                                            { Array.isArray(finalKeyPoints) && finalKeyPoints.length > 0 && (
                                                <ul style={ { paddingLeft: '20px', margin: 0 } }>
                                                    { finalKeyPoints.map((point, index) => (
                                                        <li key={ index } style={ { marginBottom: '4px' } }>{ point }</li>
                                                    )) }
                                                </ul>
                                            ) }
                                        </>
                                    );
                                })() }
                            </div>
                        ) }

                        {/* === Activity Log === */ }
                        { activeTab === 'activity' && (
                            <div style={ { padding: '0' } }>
                                { loadingActivities ? (
                                    <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#6b7280' } }>
                                        <Activity className="animate-pulse" size={ 20 } style={ { marginRight: '10px', color: '#3b82f6' } } />
                                        <span style={ { fontSize: '14px', fontWeight: 500 } }>Loading activity history...</span>
                                    </div>
                                ) : activities.length === 0 ? (
                                    <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9ca3af', flexDirection: 'column', gap: '12px' } }>
                                        <div style={ { background: '#f3f4f6', padding: '16px', borderRadius: '50%' } }>
                                            <FileText size={ 32 } opacity={ 0.4 } />
                                        </div>
                                        <div style={ { fontSize: '14px' } }>No input</div>
                                    </div>
                                ) : (
                                    (() => {
                                        const extractValue = (val) => {
                                            if (val === null || val === undefined) return null;

                                            const prettify = (str) => {
                                                if (typeof str !== 'string') return str;
                                                const s = str.trim();
                                                if (s === '') return s;
                                                // Detect and format ISO date strings
                                                if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
                                                    const d = new Date(s);
                                                    if (!isNaN(d.getTime())) {
                                                        const hasTime = s.includes('T') || s.includes(':');
                                                        const datePart = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                                                        if (hasTime) {
                                                            const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                                            return `${datePart} · ${timePart}`;
                                                        }
                                                        return datePart;
                                                    }
                                                }
                                                // Prettify snake_case identifiers
                                                if (/^[a-z0-9_]+$/.test(s) && s.includes('_')) {
                                                    return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                                                }
                                                return s;
                                            };

                                            if (typeof val === 'string') {
                                                const trimmed = val.trim();
                                                if (trimmed === '') return null;
                                                // Handle potential stringified JSON
                                                if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                                                    try {
                                                        const parsed = JSON.parse(trimmed);
                                                        return extractValue(parsed);
                                                    } catch (e) {
                                                        return prettify(trimmed);
                                                    }
                                                }
                                                return prettify(trimmed);
                                            }

                                            if (typeof val === 'object') {
                                                if (Array.isArray(val)) {
                                                    const parts = val.map(v => extractValue(v)).filter(v => v !== null);
                                                    return parts.length > 0 ? parts.join(', ') : null;
                                                }

                                                // Prioritize human-readable fields
                                                if (val.name && typeof val.name !== 'object') return prettify(String(val.name));
                                                if (val.label && typeof val.label !== 'object') return prettify(String(val.label));
                                                if (val.displayName && typeof val.displayName !== 'object') return prettify(String(val.displayName));
                                                if (val.value !== undefined && typeof val.value !== 'object') return prettify(String(val.value));
                                                if (val.email && typeof val.email !== 'object') return prettify(String(val.email));

                                                // Filter out IDs and try to find any first string value
                                                const entries = Object.entries(val).filter(([k]) => !['id', '_id', '__v'].includes(k.toLowerCase()));
                                                if (entries.length === 0) return null;

                                                // Check for "name" or "value" like fields in entries
                                                const nameEntry = entries.find(([k]) => k.toLowerCase().includes('name') || k.toLowerCase().includes('title') || k.toLowerCase().includes('label'));
                                                if (nameEntry && typeof nameEntry[1] !== 'object') return prettify(String(nameEntry[1]));

                                                // First simple value
                                                const simpleValue = entries.find(([k, v]) => typeof v !== 'object' && v !== null);
                                                if (simpleValue) return prettify(String(simpleValue[1]));

                                                // Fallback to recursive first entry if its an object
                                                if (typeof entries[0][1] === 'object') return extractValue(entries[0][1]);

                                                return null;
                                            }
                                            return prettify(String(val));
                                        };

                                        // Group by dealName, newest activity first within each group
                                        const dealGroups = {};
                                        [...activities]
                                            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                                            .forEach(activity => {
                                                const dealKey = activity.dealName || activity.dealId || 'Unknown Deal';
                                                if (!dealGroups[dealKey]) dealGroups[dealKey] = [];
                                                dealGroups[dealKey].push(activity);
                                            });

                                        // Sort deal groups by their newest activity
                                        const dealKeys = Object.keys(dealGroups).sort((a, b) =>
                                            new Date(dealGroups[b][0].timestamp) - new Date(dealGroups[a][0].timestamp)
                                        );

                                        const formatTimestamp = (ts) => {
                                            const d = new Date(ts);
                                            if (isNaN(d.getTime())) return 'Invalid date';
                                            const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                            const dateLabel = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                                            return `${dateLabel} · ${time}`;
                                        };

                                        return (
                                            <div style={ { padding: '8px 16px 16px 16px' } }>
                                                { dealKeys.map((dealKey, dIdx) => {
                                                    const dealActivities = dealGroups[dealKey];
                                                    return (
                                                        <div key={ dealKey } style={ { marginBottom: dIdx < dealKeys.length - 1 ? '32px' : '0' } }>

                                                            {/* Deal Name Heading */ }
                                                            <div style={ {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px',
                                                                marginBottom: '18px',
                                                                paddingTop: '4px',
                                                                paddingBottom: '4px',
                                                            } }>
                                                                <div style={ {
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    background: '#f0f9ff',
                                                                    border: '1px solid #bae6fd',
                                                                    borderRadius: '8px',
                                                                    padding: '6px 14px',
                                                                    flexShrink: 0,
                                                                } }>
                                                                    <Activity size={ 14 } color="#0284c7" />
                                                                    <span style={ { fontWeight: 700, color: '#0369a1', fontSize: '13px' } }>
                                                                        { dealKey }
                                                                    </span>
                                                                    <span style={ {
                                                                        background: '#0284c7',
                                                                        color: '#fff',
                                                                        borderRadius: '20px',
                                                                        fontSize: '10px',
                                                                        fontWeight: 700,
                                                                        padding: '1px 7px',
                                                                        marginLeft: '2px',
                                                                    } }>
                                                                        { dealActivities.length }{ dealActivities.length === 1 ? ' activity' : ' activities' }
                                                                    </span>
                                                                </div>
                                                                <div style={ { flex: 1, height: '1px', background: '#e0f2fe' } } />
                                                            </div>

                                                            {/* Activities under this deal — newest first */ }
                                                            <div style={ { display: 'flex', flexDirection: 'column', gap: '20px' } }>
                                                                { dealActivities.map((activity) => {
                                                                    const filteredChanges = (activity.changes || []).filter(change => {
                                                                        if (change.field === 'id' || change.field === '_id') return false;
                                                                        const newVal = extractValue(change.newValue);
                                                                        const oldVal = extractValue(change.oldValue);
                                                                        // Only show if the actual extracted text has changed
                                                                        return newVal !== null && newVal !== undefined && String(newVal).trim() !== '' && String(newVal) !== String(oldVal);
                                                                    });
                                                                    if (filteredChanges.length === 0) return null;

                                                                    return (
                                                                        <div key={ activity._id } style={ { display: 'flex', gap: '14px' } }>
                                                                            {/* Avatar */ }
                                                                            <div style={ {
                                                                                width: '36px', height: '36px', borderRadius: '50%',
                                                                                background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)',
                                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                flexShrink: 0, fontSize: '13px', fontWeight: 700, color: '#fff',
                                                                            } }>
                                                                                { activity.userId?.name ? activity.userId.name.charAt(0).toUpperCase() : 'U' }
                                                                            </div>

                                                                            <div style={ { flex: 1, minWidth: 0 } }>
                                                                                {/* Activity header */ }
                                                                                <div style={ { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '6px' } }>
                                                                                    {/* Left: name + action */ }
                                                                                    <div style={ { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' } }>
                                                                                        <span style={ { fontWeight: 700, color: '#111827', fontSize: '13px' } }>
                                                                                            { activity.userId?.name || 'Unknown User' }
                                                                                        </span>
                                                                                        <span style={ {
                                                                                            fontSize: '12px',
                                                                                            color: '#fff',
                                                                                            background: activity.action === 'updated' ? '#6366f1' : '#f59e0b',
                                                                                            borderRadius: '4px',
                                                                                            padding: '1px 7px',
                                                                                            fontWeight: 600,
                                                                                            letterSpacing: '0.01em',
                                                                                        } }>
                                                                                            { activity.action === 'updated' ? 'Updated deal' : 'Modified deal' }
                                                                                        </span>
                                                                                    </div>
                                                                                    {/* Right: single combined timestamp pill */ }
                                                                                    <span style={ {
                                                                                        display: 'inline-flex',
                                                                                        alignItems: 'center',
                                                                                        gap: '4px',
                                                                                        fontSize: '11px',
                                                                                        color: '#6b7280',
                                                                                        background: '#f3f4f6',
                                                                                        border: '1px solid #e5e7eb',
                                                                                        borderRadius: '6px',
                                                                                        padding: '3px 9px',
                                                                                        fontWeight: 500,
                                                                                        whiteSpace: 'nowrap',
                                                                                    } }>
                                                                                        <Clock size={ 11 } />
                                                                                        { formatTimestamp(activity.timestamp) }
                                                                                    </span>
                                                                                </div>

                                                                                {/* Changes card */ }
                                                                                <div style={ { background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' } }>
                                                                                    { filteredChanges.map((change, i, arr) => {
                                                                                        const oldVal = extractValue(change.oldValue);
                                                                                        const newVal = extractValue(change.newValue);
                                                                                        const hasPrev = oldVal !== null;
                                                                                        return (
                                                                                            <div key={ i } style={ {
                                                                                                padding: '10px 14px',
                                                                                                borderBottom: i === arr.length - 1 ? 'none' : '1px solid #f3f4f6',
                                                                                                display: 'flex',
                                                                                                flexDirection: 'column',
                                                                                                gap: '6px',
                                                                                            } }>
                                                                                                <div style={ { fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' } }>
                                                                                                    { formatFieldName(change.field) }
                                                                                                </div>
                                                                                                <div style={ { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' } }>
                                                                                                    { hasPrev && (
                                                                                                        <>
                                                                                                            <div style={ { flex: 1, padding: '6px 10px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '6px', color: '#b91c1c', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' } }>
                                                                                                                <span style={ { fontSize: '9px', fontWeight: 700, color: '#ef4444', textTransform: 'uppercase' } }>Previous</span>
                                                                                                                <div style={ { wordBreak: 'break-word' } }>{ oldVal }</div>
                                                                                                            </div>
                                                                                                            <ArrowRight size={ 14 } color="#9ca3af" style={ { flexShrink: 0 } } />
                                                                                                        </>
                                                                                                    ) }
                                                                                                    <div style={ { flex: 1, padding: '6px 10px', background: '#f0fdf4', border: '1px solid #dcfce7', borderRadius: '6px', color: '#15803d', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' } }>
                                                                                                        { hasPrev && <span style={ { fontSize: '9px', fontWeight: 700, color: '#22c55e', textTransform: 'uppercase' } }>New</span> }
                                                                                                        <div style={ { wordBreak: 'break-word', fontWeight: 500 } }>{ newVal }</div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        );
                                                                                    }) }
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }) }
                                                            </div>
                                                        </div>
                                                    );
                                                }) }
                                            </div>
                                        );
                                    })()
                                ) }
                            </div>
                        ) }

                        {/* === Deal Log (Deals by Customer) === */ }
                        { activeTab === 'history' && (
                            <div style={ { padding: '4px 0 8px 0' } }>
                                { loadingHistory ? (
                                    <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#6b7280', gap: '10px' } }>
                                        <TrendingUp size={ 20 } style={ { color: '#3b82f6', animation: 'pulse 1.5s infinite' } } />
                                        <span style={ { fontSize: '14px', fontWeight: 500 } }>Loading deal log...</span>
                                    </div>
                                ) : !dealHistory || !dealHistory.deals || dealHistory.deals.length === 0 ? (
                                    <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9ca3af', flexDirection: 'column', gap: '12px' } }>
                                        <div style={ { background: '#f3f4f6', padding: '20px', borderRadius: '50%' } }>
                                            <Layers size={ 28 } opacity={ 0.4 } />
                                        </div>
                                        <div style={ { fontSize: '14px', fontWeight: 500 } }>No input</div>
                                        <div style={ { fontSize: '12px', color: '#d1d5db' } }>Deals will appear here once available.</div>
                                    </div>
                                ) : (
                                    <div style={ { display: 'flex', flexDirection: 'column', gap: '0' } }>

                                        {/* Header summary bar */ }
                                        <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px 14px 4px', borderBottom: '1px solid #f3f4f6', marginBottom: '16px' } }>
                                            <div style={ { display: 'flex', alignItems: 'center', gap: '12px' } }>
                                                <div style={ { width: 40, height: 40, borderRadius: '8px', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '16px' } }>
                                                    { (dealHistory.customerName || 'C').charAt(0).toUpperCase() }
                                                </div>
                                                <div>
                                                    <div style={ { fontWeight: 700, color: '#111827', fontSize: '15px' } }>{ dealHistory.customerName || '—' }</div>
                                                </div>
                                            </div>
                                            <div style={ { display: 'flex', alignItems: 'center', gap: '6px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '20px', padding: '5px 16px' } }>
                                                <span style={ { fontSize: '12px', fontWeight: 700, color: '#166534' } }>{ dealHistory.totalDeals } Deal{ dealHistory.totalDeals !== 1 ? 's' : '' }</span>
                                            </div>
                                        </div>

                                        {/* Deal list */ }
                                        <div style={ { display: 'flex', flexDirection: 'column', gap: '16px' } }>
                                            { dealHistory.deals.map((deal, idx) => {
                                                const riskValue = (deal.riskScore !== undefined && deal.riskScore !== null) ? deal.riskScore : null;
                                                const riskNum = riskValue !== null ? Number(riskValue) : NaN;
                                                const riskColor = getRiskColor(riskNum);
                                                const riskLabel = getRiskLabel(riskValue);
                                                const meddic = deal.meddic || {};

                                                const InfoBox = ({ label, value, icon, valueColor, highlight }) => (
                                                    <div style={ {
                                                        background: highlight ? '#f0f9ff' : '#f9fafb',
                                                        border: highlight ? '1px solid #bae6fd' : '1px solid #f3f4f6',
                                                        borderRadius: '8px',
                                                        padding: '10px 12px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '4px',
                                                        minWidth: 0,
                                                        flex: 1
                                                    } }>
                                                        <div style={ { fontSize: '10px', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '4px' } }>
                                                            { icon } { label }
                                                        </div>
                                                        <div style={ {
                                                            fontSize: '13px',
                                                            fontWeight: 700,
                                                            color: valueColor || '#1f2937',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        } }>
                                                            { value || <span style={ { color: '#9ca3af', fontStyle: 'italic', fontWeight: 400 } }>No input</span> }
                                                        </div>
                                                    </div>
                                                );

                                                return (
                                                    <div
                                                        key={ deal.dealId || idx }
                                                        style={ {
                                                            background: '#fff',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '12px',
                                                            padding: '16px',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: '12px'
                                                        } }
                                                    >
                                                        {/* Row 1: 4 Boxes */ }
                                                        <div style={ { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' } }>
                                                            <InfoBox label="Deal Name" value={ deal.dealName } highlight />
                                                            <InfoBox
                                                                label="Risk Score"
                                                                value={ riskValue !== null ? `${riskNum} (${riskLabel})` : null }
                                                                valueColor={ riskColor }
                                                            />
                                                            <InfoBox label="Last Stage" value={ deal.lastActiveStage } />
                                                            <InfoBox label="Assigned To" value={ deal.assignedTo } icon={ <User size={ 10 } /> } />
                                                        </div>

                                                        {/* Row 2: 2 Boxes + MEDDIC */ }
                                                        <div style={ { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', alignItems: 'stretch' } }>
                                                            <div style={ { gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' } }>
                                                                <InfoBox label="Created" value={ formatDate(deal.createdAt) } icon={ <Calendar size={ 10 } /> } />
                                                                <InfoBox label="Last Activity" value={ formatActivityDate(deal.lastActivityDate) } icon={ <Clock size={ 10 } /> } />
                                                            </div>

                                                            {/* MEDDIC Column (Spans 2 units) */ }
                                                            <div style={ {
                                                                gridColumn: 'span 2',
                                                                background: '#f9fafb',
                                                                border: '1px solid #f3f4f6',
                                                                borderRadius: '8px',
                                                                padding: '10px 12px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '6px'
                                                            } }>
                                                                <div style={ { fontSize: '10px', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' } }>
                                                                    MEDDIC Status
                                                                </div>
                                                                <div style={ { display: 'flex', gap: '6px' } }>
                                                                    { MEDDIC_FIELDS.map((f) => {
                                                                        const fieldData = meddic[f.key];
                                                                        const done = fieldData && fieldData.present === true;
                                                                        return (
                                                                            <div
                                                                                key={ f.key }
                                                                                title={ f.label }
                                                                                style={ {
                                                                                    width: 28, height: 26,
                                                                                    borderRadius: '4px',
                                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                    fontSize: '11px', fontWeight: 700,
                                                                                    background: done ? '#22c55e' : '#e5e7eb',
                                                                                    color: done ? '#ffffff' : '#6b7280',
                                                                                    transition: 'all 0.2s ease'
                                                                                } }
                                                                            >
                                                                                { f.short }
                                                                            </div>
                                                                        );
                                                                    }) }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }) }
                                        </div>
                                    </div>
                                ) }
                            </div>
                        ) }
                    </div>
                </div>
            </div>
        </div>
    );
}
